'use client'

import React, { useState } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
    DialogFooter,
} from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField } from "@/components/ui/form"
import { Textarea } from "../../@/components/ui/textarea"
import { Plus } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { useAuth } from "@clerk/clerk-react"
import { ClipLoader } from "react-spinners";
import { Field, FieldContent, FieldError, FieldLabel } from "../../@/components/ui/field";
import { cn } from "@/lib/utils";

const addRoleSchema = z.object({
    title: z.string().min(1, "This is required"),
    description: z.string().min(1, "This is required"),
})

const AddRoleDialog = ({ onAdd, refetchRoles }) => {
    const [open, setOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { getToken } = useAuth()

    const form = useForm({
        resolver: zodResolver(addRoleSchema),
        defaultValues: {
            title: "",
            description: "",
        }
    })

    const onSubmit = async (values) => {
        try {
            setIsSubmitting(true)

            const token = await getToken()
            if (!token) {
                toast.error("Authentication required to create a role.")
                return
            }

            const payload = {
                title: values.title,
                description: values.description
            }

            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }

            // Replace with your actual role creation endpoint
            const rolesResponse = await axios.post(api_endpoint + "/user/roles/create", payload, reqConfig)

            console.log("Role created:", rolesResponse.data)
            toast.success(`Role "${values.title}" created successfully!`)
            if (onAdd) onAdd(rolesResponse.data)
            if (refetchRoles) refetchRoles() // refetch roles
            setOpen(false)
            form.reset()
        } catch (error) {
            console.error("Failed to create role:", error)
            toast.error("Failed to create role.")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="" variant="">
                    Add Role
                    <Plus className="ml-2 h-4 w-4" />
                </Button>
            </DialogTrigger>
            <DialogContent className="h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-(--neutral-900)">Add New Role</DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">
                        Fill in the details to create a new role.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field, fieldState }) => (
                                <Field>
                                    <FieldLabel>Role Title *</FieldLabel>
                                    <FieldContent>
                                        <Input
                                            placeholder="e.g. ADMIN"
                                            {...field}
                                            className={cn(
                                                "font-body",
                                                fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                            )}
                                        />
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field, fieldState }) => (
                                <Field>
                                    <FieldLabel>Role Description *</FieldLabel>
                                    <FieldContent>
                                        <Textarea
                                            placeholder="Describe the role..."
                                            rows={3}
                                            {...field}
                                            className={cn(
                                                "font-body resize-none",
                                                fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                                            )}
                                        />
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />
                        <DialogFooter className="">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setOpen(false)}
                                className="">
                                Cancel
                            </Button>
                            <Button
                                variant=""
                                type="submit"
                                className=""
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center w-full">
                                        <ClipLoader size={20} color="#fff" />
                                    </span>
                                ) : (
                                    "Add Role"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddRoleDialog
